import React, { useEffect, useState } from "react";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ClearIcon from "@mui/icons-material/ClearOutlined";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import SearchIcon from "@mui/icons-material/Search";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Avatar from "@mui/material/Avatar/Avatar";
import Backdrop from "@mui/material/Backdrop/Backdrop";
import Box from "@mui/material/Box/Box";
import Card from "@mui/material/Card/Card";
import CardActionArea from "@mui/material/CardActionArea/CardActionArea";
import CircularProgress from "@mui/material/CircularProgress/CircularProgress";
import Grid from "@mui/material/Grid/Grid";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Skeleton from "@mui/material/Skeleton/Skeleton";
import Stack from "@mui/material/Stack/Stack";
import TextField from "@mui/material/TextField/TextField";
import Typography from "@mui/material/Typography/Typography";
import { NumericFormat } from "react-number-format";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { makeStyles } from "tss-react/mui";

import BookingChildSelection from "./BookingChildSelection";
import MissingMMPDocMessage from "./MissingMMPDocMessage";
import NationalProgramSelection from "./NationalProgramSelection";
import VacProgramSelection from "./VacProgramSelection";
import { useAppDispatch } from "../../store/hooks";
import {
  LoadCreateCasualBookingsPreviewInitial,
  SaveBookingInitial,
} from "../../store/modules/booking/bookingStateActions";
import { getChildren } from "../../store/modules/child/childActions";
import {
  getCustomerServicesDigitalProgramsSummary,
  getServicesWithUpcomingDigitalPrograms,
} from "../../store/modules/digitalProgram/digitalProgramActions";
import { serviceProgramsReset } from "../../store/modules/program/programStateActions";
import { LoadedUnavailableDatesReset } from "../../store/modules/session/sessionStateAction";
import { RootState } from "../../store/store";
import { Child, ChildAgeConstraint, EntityType, RollDetails, SelectedChild } from "../../types/models";
import {
  IServiceWithUpcomingProgram,
  ISelectedChild,
  IServiceSimpleModel,
  ISessionAvailability,
  RocketeersBookingInfo,
  IPageableObject,
  IBookingProgram,
} from "../../types/types";
import { getStyleColor } from "../../utils/helpers";
import { isChildBlockedForAllServices, isChildInMissingDocumentationStatus } from "../../utils/medicalConditionsHelper";
import { ButtonPrimary, ButtonSecondary } from "../Common/Buttons/Buttons";
import Spacer from "../Common/Spacers/Spacers";

interface IProps {
  formState: RocketeersBookingInfo;
  onAddBooking: (program: IBookingProgram) => void;
  onRemoveBooking: (program: IBookingProgram) => void;
  onServiceSelected: (service: IServiceSimpleModel) => void;
  onUnSelectService: () => void;
  onChildSelectionChanged: (child: Child, isAlreadySelected: boolean, ageIsGreater: boolean) => void;
  onChildReasonChanged(childId: number, reason: string): void;
  onChildrenRefresh(children: ISelectedChild[]): void;
  pageState: {
    hasConflict: boolean;
    hasError: boolean;
    unAvailableDates: ISessionAvailability[];
    newlyAddedUnAvailableDates: ISessionAvailability[];
  };
  setPageState: React.Dispatch<
    React.SetStateAction<{
      hasConflict: boolean;
      hasError: boolean;
      unAvailableDates: ISessionAvailability[];
      newlyAddedUnAvailableDates: ISessionAvailability[];
    }>
  >;
}

const useVacStyles = makeStyles()((theme) => {
  return {
    tab: {
      minWidth: "250px",
      [theme.breakpoints.down("sm")]: {
        minWidth: "150px",
      },
      padding: "1rem",
      borderRadius: "0.5rem",
    },
    rocketeer: {
      backgroundColor: "white",
      border: "2px solid var(--custom-color-rocketeer)",
      "&.Mui-selected": {
        backgroundColor: "var(--custom-color-rocketeer)!important",
      },
    },
    rocketeerDisabled: {
      backgroundColor: "gray",
      opacity: 0.5,

      "&.Mui-selected": {
        backgroundColor: "var(--custom-color-rocketeer)!important",
      },
    },

    logo: {
      height: 40,
      "@media screen and (min-width: 720px)": {
        height: 40,
      },
    },
    logoRocketeer: {
      height: 50,
    },
    icon: {
      width: "3rem",
      height: "3rem",
    },
    childName: {
      fontWeight: "bold",
      textDecoration: "underline",
    },

    button: {
      pointerEvents: "auto",
    },
    selected: {
      border: "2px solid var(--custom-color-rocketeer)",
      backgroundColor: "#daf3ff",
    },
    selectedVac: {
      border: "2px solid var(--custom-color-rocketeer)",
      backgroundColor: "#daf3ff",
    },

    unselected: {
      border: "1px solid #00000029",
    },
    different: {
      "& .MuiCardActionArea-root": {
        opacity: 0.5,
      },
      border: "1px solid #00000029",
    },
    dayAvailable: {
      backgroundColor: "white",
      border: "1px solid black",
      color: "black",
    },

    daySelected: {
      backgroundColor: "var(--youroshc-color-primary)",
      border: "1px solid black",
      color: "black",
    },

    dayDisabled: {
      backgroundColor: "#DDDDDD",
      border: "1px solid white",
      color: "white",
    },
    accordion: {
      backgroundColor: getStyleColor("--custom-color-light-grey"),
      border: "1px solid var(--youroshc-color-primary)",
      borderRadius: "10px",
      marginBottom: theme.spacing(1),
      "&::before": {
        display: "none",
      },
    },

    accordionVac: {
      backgroundColor: getStyleColor("--custom-color-light-grey"),
      borderRadius: "10px",
      margin: "0px 0px 10px 0px !important",
      "&::before": {
        display: "none",
      },
    },

    accordionError: {
      backgroundColor: getStyleColor("--custom-color-light-grey"),

      borderRadius: "10px",
      marginBottom: theme.spacing(1),

      border: "1px solid #b42931",
      borderLeftWidth: 5,
    },
    backdrop: {
      zIndex: theme.zIndex.drawer + 1,
      color: "white",
    },
    section: {
      border: "1px solid var(--youroshc-color-primary)",
      borderRadius: "10px",
    },
    sectionVac: {
      border: "1px solid var(--custom-color-rocketeer)",
      borderRadius: "10px",
    },

    stack: {
      marginLeft: "5px!important",
      "& .MuiStack-root": {
        marginLeft: "5px!important",
      },
    },
    accordionSummary: {
      alignItems: "flex-start",
      borderRadius: "10px",
      backgroundColor: "white",
    },
    stepYourOSHC: {
      color: "var(--youroshc-color-primary)",
      width: "40px",
      height: "40px",
    },

    stepDisabled: {
      color: "grey",
      width: "40px",
      height: "40px",
    },
    stepVac: {
      color: "var(--custom-color-rocketeer)",
      width: "40px",
      height: "40px",
    },

    locButton: {
      height: 55,
    },
  };
});

export default function RocketeersBookingEditor(props: IProps) {
  const {
    formState,
    onAddBooking,
    onRemoveBooking,
    onServiceSelected,
    onChildSelectionChanged,
    onChildReasonChanged,
    onChildrenRefresh,
    onUnSelectService,
    pageState,
    setPageState,
  } = props;
  const { classes } = useVacStyles();

  const dispatch = useAppDispatch();

  const [currentStep, setcurrentStep] = useState<number | undefined>(0);
  const register = useSelector((state: RootState) => state.register);
  const auth = useSelector((state: RootState) => state.auth);
  const profile = auth.user?.profile;

  const [servicesProgramSummary, setServicesProgramSummary] = useState<IPageableObject<IServiceWithUpcomingProgram>>({
    records: [],
    total: 0,
  });
  const customerAccount = useSelector((state: RootState) => state.customer.customer);

  const children = useSelector((state: RootState) => state.child.childrenByCustomer);

  const avatars = useSelector((state: RootState) =>
    state.avatars?.avatars?.filter((x) => x.entityType === EntityType.Child)
  );

  const handleChange = (step: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setcurrentStep(isExpanded ? step : undefined);
  };

  const [searchText, setSearchText] = useState<{ query: string; trigger: boolean; skip: number }>({
    query: "",
    trigger: false,
    skip: 0,
  });

  useEffect(() => {
    //trigger search after 1 second when user stops typing
    const search = setTimeout(() => {
      onSearchTextChanged();
    }, 1000);
    return () => clearTimeout(search);
  }, [searchText.query]);
  useEffect(() => {
    if (searchText.trigger) {
      onSearchTextChanged();
      setSearchText({ ...searchText, trigger: false });
    }
  }, [searchText.trigger]);

  function clearSearchText() {
    setSearchText({ query: "", trigger: false, skip: 0 });
  }

  var servicesProgramSummaryResult = useQuery(
    ["getCustomerServicesDigitalProgramsSummary", profile?.customer_account_id],
    () => getCustomerServicesDigitalProgramsSummary(profile!.customer_account_id!),
    {
      refetchOnWindowFocus: false,
      enabled: profile?.customer_account_id !== undefined,
    }
  );

  var servicesProgramsByLocationResult = useQuery(
    ["getServicesWithUpcomingDigitalPrograms", profile?.customer_account_id, searchText.query, searchText.skip],
    () => getServicesWithUpcomingDigitalPrograms(profile!.customer_account_id!, searchText.query, 4, searchText.skip),
    {
      refetchOnWindowFocus: false,
      enabled: profile?.customer_account_id !== undefined && searchText.query.length > 2,
    }
  );

  var isServicesLoading = servicesProgramSummaryResult.isLoading || servicesProgramsByLocationResult.isLoading;
  useEffect(() => {
    if (servicesProgramsByLocationResult.isSuccess && servicesProgramsByLocationResult.data) {
      if (searchText.skip === 0) {
        setServicesProgramSummary({
          records: servicesProgramsByLocationResult.data.records,
          total: servicesProgramsByLocationResult.data.total,
        });
      } else {
        setServicesProgramSummary({
          records: servicesProgramSummary.records.concat(servicesProgramsByLocationResult.data.records),
          total: servicesProgramsByLocationResult.data.total,
        });
      }
    }
  }, [servicesProgramsByLocationResult.status]);
  const onSearchTextChanged = () => {
    if (isServicesLoading) return;
    if (searchText.query.length > 2) {
      setServicesProgramSummary({
        records: [],
        total: 0,
      });
    }
  };

  useEffect(() => {
    if (servicesProgramSummaryResult.isSuccess && servicesProgramSummaryResult.data) {
      setServicesProgramSummary({
        records: servicesProgramSummaryResult.data,
        total: servicesProgramSummaryResult.data.length,
      });
    }
  }, [servicesProgramSummaryResult.status]);

  function handleUseMyLocation() {
    if (customerAccount) {
      var cont = customerAccount.contacts?.find((c) => c.isPrimary);
      if (cont) {
        var addr = cont.residentialAddress;
        setSearchText({
          query: addr?.streetLine1 + ", " + addr?.suburb + ", " + addr?.state + " " + addr?.postCode,
          trigger: false,
          skip: 0,
        });
      }
    }
  }

  function handleServiceSelected(svc: IServiceSimpleModel) {
    dispatch(serviceProgramsReset());
    if (formState.service?.serviceId === svc.serviceId) {
      onUnSelectService();
    } else {
      onServiceSelected(svc);
      //move to next section
      setcurrentStep(1);
    }
  }

  useEffect(() => {
    if (profile?.customer_account_id) {
      dispatch(SaveBookingInitial());
      dispatch(LoadedUnavailableDatesReset());
      dispatch(LoadCreateCasualBookingsPreviewInitial());
    }
  }, [profile?.customer_account_id, formState.service]);

  useQuery(["getChildren", profile?.customer_account_id], () => getChildren(dispatch, profile!.customer_account_id!), {
    refetchOnWindowFocus: false,
    enabled: profile?.customer_account_id !== undefined && children !== null,
  });

  function isAgeGreaterThanMax(constraints: ChildAgeConstraint[], child: SelectedChild) {
    return constraints.filter((ct) => ct !== undefined && ct.isAgeGreaterThanMax(child));
  }

  function refreshMaxAgeViolation() {
    const constraints = getConstraints();

    const children = formState.children.map((c) => {
      const violations = isAgeGreaterThanMax(constraints, new SelectedChild(c));
      c.reasonRequired = violations.length > 0;
      return c;
    });

    onChildrenRefresh(children);
  }
  useEffect(() => {
    refreshMaxAgeViolation();
  }, [formState.dates]);

  function StepHeader(title: string, index: number, isComplete: boolean) {
    return (
      <Stack direction="row" spacing={2} alignItems="center">
        {!isComplete && (
          <Avatar sx={{ width: 30, height: 30, bgcolor: "white", color: "black", border: "2px solid black" }}>
            {index + 1}
          </Avatar>
        )}
        {isComplete && <CheckCircleIcon className={classes.stepVac} />}{" "}
        <Grid container direction="column" justifyItems={"flex-start"}>
          <Typography variant="h4" fontWeight={"bold"}>
            {title}
          </Typography>

          {currentStep !== 1 &&
            index === 1 &&
            formState.dates.length > 0 &&
            formState.dates.map((d, index) => {
              return (
                <Grid container direction={"row"} columnGap={1} margin={1} ml={0} key={index}>
                  <Typography variant="subtitle2">{d.item.programName}</Typography>

                  <Typography variant="subtitle2">
                    <strong>
                      <NumericFormat
                        displayType="text"
                        value={
                          isFriendlyFee() || !d.item.isCasualFee
                            ? d.item.session.primaryFeeAmount
                            : d.item.session.casualFeeAmount
                        }
                        decimalScale={2}
                        fixedDecimalScale={true}
                        prefix="$"
                        suffix=""
                      />
                    </strong>
                  </Typography>
                </Grid>
              );
            })}

          {currentStep !== 0 && index === 0 && formState.service && (
            <Stack direction="row" spacing={2}>
              <LocationOnOutlinedIcon fontSize="small" />
              <Typography variant="subtitle2"> {formState.service.serviceName}</Typography>
            </Stack>
          )}
        </Grid>
      </Stack>
    );
  }

  function isFriendlyFee() {
    return customerAccount?.isFriendlyFeeEligible === true;
  }

  function getConstraints() {
    return formState.dates.map(
      (d) =>
        new ChildAgeConstraint({
          rollDate: d.item.date,
          session: new RollDetails(d.item.session),
          isExcursion: d.item.isExcursion,
        })
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Backdrop className={classes.backdrop} open={isServicesLoading}>
        <CircularProgress color="primary" />
      </Backdrop>
      <Accordion
        sx={{ border: "2px solid var(--custom-color-rocketeer)" }}
        className={classes.accordionVac}
        elevation={1}
        expanded={currentStep === 0}
        onChange={handleChange(0)}
      >
        <AccordionSummary className={classes.accordionSummary} expandIcon={<ExpandMoreIcon />}>
          <Grid container direction={"column"}>
            {StepHeader("Service Location", 0, formState.service !== undefined)}

            <Spacer margin={5} />
          </Grid>
        </AccordionSummary>
        <AccordionDetails sx={{ bgcolor: "background.paper" }}>
          <Grid container direction={"column"} spacing={2}>
            <Grid item xs={12}>
              <Grid container spacing={1} direction={"row"} justifyContent="space-between" alignItems={"center"}>
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    sx={{
                      "& fieldset": {
                        borderRadius: "10px",
                        backgroundColor: "#c7c5c5",
                        opacity: 0.2,
                      },
                    }}
                    value={searchText.query}
                    placeholder="School name, suburb or postcode"
                    variant="outlined"
                    onChange={async (event: React.ChangeEvent<HTMLInputElement>) => {
                      setSearchText({
                        query: event.target.value,
                        trigger: false,
                        skip: 0,
                      });
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),

                      endAdornment:
                        searchText.query.length > 0 ? (
                          <InputAdornment position="end">
                            <IconButton onClick={() => clearSearchText()}>
                              <ClearIcon />
                            </IconButton>
                          </InputAdornment>
                        ) : null,
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <ButtonSecondary
                    variant="outlined"
                    fullWidth
                    className={classes.locButton}
                    onClick={handleUseMyLocation}
                    startIcon={<MyLocationIcon htmlColor="black" />}
                  >
                    Use my location
                  </ButtonSecondary>
                </Grid>

                <Grid item xs={12}>
                  <Grid container direction="column">
                    <Typography variant="h6" fontWeight={"bold"}>
                      My schools
                    </Typography>
                    <Grid container direction="row" spacing={2}>
                      {servicesProgramSummary.records.map((s, index) => {
                        return (
                          <Grid item xs={12} md={6} key={index}>
                            {isServicesLoading && <Skeleton variant="rectangular" height={130} />}
                            {!isServicesLoading && (
                              <Card
                                key={index}
                                elevation={1}
                                className={
                                  formState.service?.serviceId === s.service.serviceId
                                    ? classes.selectedVac
                                    : s.programsCount === 0
                                    ? classes.different
                                    : classes.unselected
                                }
                              >
                                <CardActionArea
                                  sx={{ height: 130 }}
                                  onClick={s.programsCount > 0 ? () => handleServiceSelected(s.service) : undefined}
                                >
                                  <Grid container direction={"row"} alignItems="center" p={1}>
                                    <Grid item xs={10}>
                                      <Grid container direction="column" padding={1} rowGap={1}>
                                        <Typography
                                          variant="h6"
                                          fontSize={{
                                            lg: "20px",
                                            md: "16px",
                                            xs: "15px",
                                          }}
                                          className={classes.childName}
                                        >
                                          {s.service.serviceName}
                                        </Typography>

                                        <Stack direction="row" spacing={2}>
                                          <EventAvailableIcon fontSize="small" htmlColor="#c5c5c5" />
                                          <NumericFormat
                                            value={s.distanceKM}
                                            prefix={`${s.programsCount} events available | `}
                                            displayType={"text"}
                                            decimalScale={2}
                                            thousandSeparator={true}
                                            suffix=" KM"
                                          />
                                        </Stack>

                                        <Stack direction="row" spacing={2}>
                                          <LocationOnOutlinedIcon fontSize="small" htmlColor="#c5c5c5" />
                                          <Typography variant="body2">
                                            {s.service.primaryAddressSuburb} {s.service.primaryAddressState}{" "}
                                            {s.service.primaryAddressPostcode}
                                          </Typography>
                                        </Stack>
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                </CardActionArea>
                              </Card>
                            )}
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Grid>
                </Grid>

                {servicesProgramSummary.records.length < servicesProgramSummary.total && (
                  <Grid item xs={12}>
                    <Grid container direction="row" justifyContent="center" pt={5}>
                      <ButtonPrimary
                        sx={{
                          backgroundColor: "#85D6FF",
                          color: "black",
                          float: "right",
                          width: "200px",
                          fontWeight: "bold",
                        }}
                        className={classes.button}
                        variant="outlined"
                        onClick={() => setSearchText({ ...searchText, trigger: true, skip: searchText.skip + 4 })}
                      >
                        Load more
                      </ButtonPrimary>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <ButtonPrimary
                className={classes.button}
                disabled={formState.service === undefined}
                onClick={() => {
                  setcurrentStep(1);
                }}
                sx={{
                  backgroundColor: "#85D6FF",
                  color: "black",
                  float: "right",
                  width: "200px",
                  fontWeight: "bold",
                }}
              >
                Continue
              </ButtonPrimary>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Accordion
        className={classes.accordionVac}
        elevation={1}
        expanded={currentStep === 1}
        sx={{ border: "2px solid var(--custom-color-rocketeer)" }}
        onChange={formState.service === undefined ? undefined : handleChange(1)}
      >
        <AccordionSummary className={classes.accordionSummary} expandIcon={<ExpandMoreIcon />}>
          <Grid container direction={"column"}>
            {StepHeader("Child/ren", 1, formState.children.length > 0)}

            <Spacer margin={5} />
          </Grid>
        </AccordionSummary>
        <AccordionDetails sx={{ bgcolor: "background.paper" }}>
          <Grid container direction={"row"} spacing={2}>
            {children?.map((child) => {
              const avatar = avatars.find((x) => x.entityId === child?.childId?.toString());
              const selected = formState.children.find((x) => x.child.childId === child.childId);
              const isSuspended = child.isSuspended ?? false;
              const isBlocked = isChildBlockedForAllServices(child, formState.service?.serviceId, register);
              var hasMissingMMPDocuments = isChildInMissingDocumentationStatus(child);

              return (
                <Grid item xs={12} sm={6} key={child.childId}>
                  <BookingChildSelection
                    overlayBackground="var(--custom-color-rocketeer)"
                    isDifferentService={false}
                    isBlocked={isBlocked}
                    hasMissingMMPDocuments={hasMissingMMPDocuments}
                    avatar={avatar}
                    child={child}
                    classes={classes}
                    constraints={getConstraints()}
                    selected={selected}
                    onChildSelectionChanged={(ageIsGreater) => {
                      if (isSuspended) return;

                      onChildSelectionChanged(child, selected ? true : false, ageIsGreater);
                    }}
                  />
                  {isBlocked && (
                    <span style={{ color: "Red" }}>
                      Bookings are currently unavailable, but we'll be in touch with you within the next 2 days to
                      discuss the best options for your child.
                    </span>
                  )}
                  {!isBlocked && hasMissingMMPDocuments && (
                    <span style={{ color: "Red" }}>{MissingMMPDocMessage(child.childId!)}</span>
                  )}
                </Grid>
              );
            })}

            <Grid item xs={12}>
              <ButtonPrimary
                disabled={formState.children.length === 0}
                className={classes.button}
                onClick={() => {
                  setcurrentStep(2);
                }}
                sx={{
                  backgroundColor: getStyleColor("--custom-color-rocketeer"),
                  color: "black",
                  float: "right",
                  width: "200px",
                  fontWeight: "bold",
                }}
              >
                Continue
              </ButtonPrimary>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Accordion
        className={classes.accordionVac}
        sx={{ border: "2px solid var(--custom-color-rocketeer)" }}
        elevation={1}
        expanded={currentStep === 2}
        onChange={formState.children.length === 0 ? () => {} : handleChange(2)}
      >
        <AccordionSummary className={classes.accordionSummary} expandIcon={<ExpandMoreIcon />}>
          <Grid container direction={"column"}>
            {StepHeader("Learning Events", 2, formState.dates.length > 0)}

            <Spacer margin={5} />
          </Grid>
        </AccordionSummary>
        <AccordionDetails sx={{ bgcolor: "background.paper" }}>
          {formState.service?.isNationalProgrammingEnabled ? (
            <NationalProgramSelection
              constraints={getConstraints()}
              formState={formState}
              onAddBooking={onAddBooking}
              onChildReasonChanged={onChildReasonChanged}
              onContinueClicked={() => {
                setcurrentStep(3);
              }}
              onRemoveBooking={onRemoveBooking}
              pageState={pageState}
              setPageState={setPageState}
            />
          ) : (
            <VacProgramSelection
              constraints={getConstraints()}
              formState={formState}
              onAddBooking={onAddBooking}
              onChildReasonChanged={onChildReasonChanged}
              onContinueClicked={() => {
                setcurrentStep(3);
              }}
              onRemoveBooking={onRemoveBooking}
              pageState={pageState}
              setPageState={setPageState}
            />
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
